import React from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import Carousel from './Carousel';
import { useLanguage } from '../contexts/LanguageContext';
import translations from '../translations';
import kimsImage from '../images/kims.png';
import kimsExperteImage from '../images/kims_experte.png';
import peertubeImage from '../images/peertube.png';
import moodleImage from '../images/Moodle.png';

const HeroSection = styled.section`
  padding: 140px 0 80px;
  background-color: ${props => props.theme === 'dark' ? '#121212' : '#ffffff'};
  position: relative;
  overflow: hidden;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
`;

const HeroContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 2;
  box-sizing: border-box;
  
  @media (min-width: 576px) {
    padding: 0 30px;
  }
  
  @media (min-width: 992px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding: 0 40px;
  }
`;

const HeroContent = styled(motion.div)`
  text-align: center;
  max-width: 600px;
  margin-bottom: 40px;
  width: 100%;
  
  @media (min-width: 992px) {
    text-align: left;
    margin-bottom: 0;
    flex: 1;
    margin-right: 40px;
  }
`;

const HeroTitle = styled(motion.h1)`
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  color: ${props => props.theme === 'dark' ? '#f8f9fa' : '#333'};
  padding: 0;
  word-wrap: break-word;
  overflow-wrap: break-word;
  hyphens: auto;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  
  @media (min-width: 576px) {
    font-size: 2.5rem;
  }
  
  @media (min-width: 768px) {
    font-size: 3.5rem;
  }
`;

const Highlight = styled.span`
  color: #4a6cf7;
  font-weight: 700;
  white-space: normal;
  overflow-wrap: break-word;
  word-wrap: break-word;
`;

const HeroSubtitle = styled(motion.p)`
  font-size: 1rem;
  color: ${props => props.theme === 'dark' ? '#ced4da' : '#6c757d'};
  margin-bottom: 2rem;
  padding: 0;
  word-wrap: break-word;
  overflow-wrap: break-word;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  
  @media (min-width: 576px) {
    font-size: 1.1rem;
  }
  
  @media (min-width: 768px) {
    font-size: 1.25rem;
  }
`;

const HeroButtons = styled(motion.div)`
  display: flex;
  gap: 15px;
  margin-bottom: 2rem;
  justify-content: center;
  
  @media (min-width: 992px) {
    justify-content: flex-start;
  }
`;

const PrimaryButton = styled(motion.button)`
  background-color: #4a6cf7;
  color: white;
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  border: none;
  outline: none;
  box-shadow: 0 4px 6px rgba(74, 108, 247, 0.2);
  transition: all 0.3s ease;
  
  &:hover {
    background-color: #3a56d4;
    transform: translateY(-2px);
    box-shadow: 0 6px 12px rgba(74, 108, 247, 0.3);
  }
`;

const SecondaryButton = styled(motion.button)`
  background-color: transparent;
  color: ${props => props.theme === 'dark' ? '#f8f9fa' : '#333'};
  padding: 10px 22px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  border: 2px solid ${props => props.theme === 'dark' ? '#343a40' : '#e9ecef'};
  outline: none;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: ${props => props.theme === 'dark' ? '#343a40' : '#e9ecef'};
    transform: translateY(-2px);
  }
`;

const CarouselWrapper = styled.div`
  width: 100%;
  max-width: 500px;
  
  @media (min-width: 992px) {
    flex: 1;
  }
`;

const HeroWave = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 100px;
  background-image: url('data:image/svg+xml;utf8,<svg viewBox="0 0 1440 100" xmlns="http://www.w3.org/2000/svg"><path d="M0,50 C150,100 350,0 500,50 C650,100 850,0 1000,50 C1150,100 1350,0 1440,50 L1440,100 L0,100 Z" fill="${props => props.theme === 'dark' ? '%231e1e1e' : '%23f8f9fa'}"/></svg>');
  background-size: cover;
  background-repeat: no-repeat;
`;

const Hero = ({ theme }) => {
  const { language } = useLanguage();
  const t = translations.hero[language];
  
  const carouselImages = [
    kimsImage,
    kimsExperteImage,
    peertubeImage,
    moodleImage
  ];

  return (
    <HeroSection theme={theme}>
      <HeroContainer>
        <HeroContent
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <HeroTitle
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            theme={theme}
          >
            {t.title}
          </HeroTitle>
          <HeroSubtitle
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            theme={theme}
          >
            {t.subtitle}
          </HeroSubtitle>
          <HeroButtons
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6, duration: 0.8 }}
            id="get-started"
          >
            <PrimaryButton 
              as="a"
              href="#contact"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {t.getStarted}
            </PrimaryButton>
            {/* Watch Demo Button entfernt */}
          </HeroButtons>
        </HeroContent>
        
        <CarouselWrapper>
          <Carousel images={carouselImages} theme={theme} />
        </CarouselWrapper>
      </HeroContainer>
      <HeroWave theme={theme} />
    </HeroSection>
  );
};

export default Hero;
